import { useEffect, useRef, useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Calendar01Icon,
  Tick01Icon,
  Cancel01Icon,
} from '@hugeicons/core-free-icons'
import { format } from 'date-fns'

import { useAssessmentsFilterStore } from '../stores/assessmentsFilterStore'
import type { AssessmentFilters } from './AssessmentsTable'
import type { DateRange } from 'react-day-picker'
import { useDebounced } from '@/hooks/use-debounced'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { TableHead, TableRow } from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { AvatarWithInitials } from '@/components/common/avatar-with-initials'
import { useMyDirectReports } from '@/features/direct-reports/services/direct-reports.convex'

interface AssessmentsFilterRowProps {
  isDirectReportsView: boolean
  onFilterChange: (filters: AssessmentFilters) => void
}

export function AssessmentsFilterRow({
  isDirectReportsView,
  onFilterChange,
}: AssessmentsFilterRowProps) {
  const filters = useAssessmentsFilterStore((state) => state.filters)
  const setFilters = useAssessmentsFilterStore((state) => state.setFilters)
  const resetFilters = useAssessmentsFilterStore((state) => state.resetFilters)

  // Local state for the name input to allow typing without immediate filtering (non-direct-reports view)
  const [nameInput, setNameInput] = useState(filters.employeeName || '')
  const debouncedName = useDebounced(nameInput, 500)

  // Direct-reports multi-select state (client-side filter)
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<Array<string>>(
    [],
  )

  // Stable ref for onFilterChange to avoid dep instability from un-memoized callback prop
  const onFilterChangeRef = useRef(onFilterChange)
  onFilterChangeRef.current = onFilterChange

  // Load direct reports options only for direct-reports view
  const {
    results: directReports,
    status: drStatus,
    loadMore: drLoadMore,
  } = useMyDirectReports()
  const canLoadMoreDr = drStatus === 'CanLoadMore'
  const isLoadingMoreDr = drStatus === 'LoadingMore'

  // Update the filter when the debounced value changes (non-direct-reports view)
  useEffect(() => {
    if (isDirectReportsView) return
    const { filters: currentFilters, setFilters: storeSetFilters } =
      useAssessmentsFilterStore.getState()
    const normalizedName = debouncedName || undefined
    if (normalizedName === currentFilters.employeeName) return
    const newFilters = { ...currentFilters, employeeName: normalizedName }
    storeSetFilters(newFilters)
    onFilterChangeRef.current(newFilters)
  }, [debouncedName, isDirectReportsView])

  // Update the filter when selected employee IDs change (direct-reports view)
  useEffect(() => {
    if (!isDirectReportsView) return
    const { filters: currentFilters, setFilters: storeSetFilters } =
      useAssessmentsFilterStore.getState()
    const newEmployeeUserIds =
      selectedEmployeeIds.length > 0 ? selectedEmployeeIds.join(',') : undefined
    if (newEmployeeUserIds === currentFilters.employeeUserIds) return
    const newFilters = {
      ...currentFilters,
      employeeUserIds: newEmployeeUserIds,
    }
    storeSetFilters(newFilters)
    onFilterChangeRef.current(newFilters)
  }, [selectedEmployeeIds, isDirectReportsView])

  const handleFilterChange = (key: keyof AssessmentFilters, value: string) => {
    const newFilters = { ...filters, [key]: value || undefined }
    setFilters(newFilters)
    onFilterChange(newFilters)
  }

  const handleClearFilters = () => {
    setNameInput('')
    setSelectedEmployeeIds([])
    resetFilters()
    onFilterChange({})
  }

  const hasActiveFilters =
    (!!filters.employeeName && !isDirectReportsView) ||
    selectedEmployeeIds.length > 0 ||
    filters.status ||
    filters.dateFrom ||
    filters.dateTo

  const handleDateRangeChange = (dateRange: DateRange | undefined) => {
    const newFilters = {
      ...filters,
      dateFrom: dateRange?.from
        ? format(dateRange.from, 'yyyy-MM-dd')
        : undefined,
      dateTo: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
    }
    setFilters(newFilters)
    onFilterChange(newFilters)
  }

  const dateRange: DateRange | undefined =
    filters.dateFrom || filters.dateTo
      ? {
          from: filters.dateFrom ? new Date(filters.dateFrom) : undefined,
          to: filters.dateTo ? new Date(filters.dateTo) : undefined,
        }
      : undefined

  return (
    <TableRow className="border-b bg-muted/20">
      <TableHead className="px-2 py-2">
        {isDirectReportsView ? (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="h-8 w-full justify-start text-left font-normal text-sm gap-2 overflow-hidden"
              >
                {selectedEmployeeIds.length === 0 ? (
                  <span className="text-muted-foreground">Select users...</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {selectedEmployeeIds
                      .map((id) => directReports.find((d) => d.userId === id))
                      .filter(Boolean)
                      .slice(0, 3)
                      .map((dr) => (
                        <Badge
                          key={dr!._id}
                          variant="secondary"
                          className="flex items-center gap-1"
                        >
                          {dr!.name}
                          <button
                            type="button"
                            title={`Remove ${dr!.name ?? ''}`}
                            onClick={(e) => {
                              e.preventDefault()
                              setSelectedEmployeeIds((prev) =>
                                prev.filter((x) => x !== dr!.userId),
                              )
                            }}
                            className="ml-1 hover:text-foreground/80"
                          >
                            <HugeiconsIcon
                              icon={Cancel01Icon}
                              className="h-3 w-3"
                            />
                          </button>
                        </Badge>
                      ))}
                    {selectedEmployeeIds.length > 3 && (
                      <Badge variant="secondary">
                        +{selectedEmployeeIds.length - 3} more
                      </Badge>
                    )}
                  </div>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-2" align="start">
              <div className="mb-2">
                <Input
                  placeholder="Search users..."
                  className="h-8"
                  onChange={(e) => {
                    const term = e.target.value.toLowerCase()
                    const container = e.currentTarget.parentElement
                      ?.nextElementSibling as HTMLDivElement
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    if (!container) return
                    for (const row of Array.from(
                      container.querySelectorAll('[data-user-row]'),
                    )) {
                      const name = (row as HTMLElement).dataset.name || ''
                      const email = (row as HTMLElement).dataset.email || ''
                      const match = name.includes(term) || email.includes(term)
                      ;(row as HTMLElement).style.display = match ? '' : 'none'
                    }
                  }}
                />
              </div>
              <div
                className="max-h-64 overflow-auto pr-1"
                onScroll={(e) => {
                  const el = e.currentTarget
                  if (
                    canLoadMoreDr &&
                    !isLoadingMoreDr &&
                    el.scrollTop + el.clientHeight >= el.scrollHeight - 40
                  ) {
                    drLoadMore(20)
                  }
                }}
              >
                {directReports.map((dr) => {
                  const selected = selectedEmployeeIds.includes(dr.userId)
                  return (
                    <div
                      key={dr._id}
                      data-user-row
                      data-name={(dr.name ?? '').toLowerCase()}
                      data-email={(dr.email ?? '').toLowerCase()}
                      className="flex items-center gap-3 px-2 py-1.5 hover:bg-muted/50 rounded"
                    >
                      <Checkbox
                        id={`dr-${dr.userId}`}
                        checked={selected}
                        onCheckedChange={() =>
                          setSelectedEmployeeIds((prev) =>
                            prev.includes(dr.userId)
                              ? prev.filter((x) => x !== dr.userId)
                              : [...prev, dr.userId],
                          )
                        }
                      />
                      <label
                        htmlFor={`dr-${dr.userId}`}
                        className="flex-1 flex items-center gap-3 cursor-pointer"
                      >
                        <AvatarWithInitials
                          name={dr.name ?? dr.email ?? '?'}
                          src={dr.image ?? undefined}
                          className="h-6 w-6"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-sm">{dr.name}</div>
                          <div className="truncate text-xs text-muted-foreground">
                            {dr.email}
                          </div>
                        </div>
                      </label>
                      {selected && (
                        <HugeiconsIcon
                          icon={Tick01Icon}
                          className="h-4 w-4 text-primary"
                        />
                      )}
                    </div>
                  )
                })}
                {isLoadingMoreDr && (
                  <div className="text-xs text-muted-foreground p-2 text-center">
                    Loading more...
                  </div>
                )}
                {directReports.length === 0 && (
                  <div className="text-sm text-muted-foreground p-2">
                    No direct reports
                  </div>
                )}
              </div>
              {selectedEmployeeIds.length > 0 && (
                <div className="pt-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="h-8"
                    onClick={() => {
                      setSelectedEmployeeIds([])
                    }}
                  >
                    Clear selected
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        ) : (
          <Input
            placeholder="Filter name..."
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            className="h-8 text-sm bg-background"
          />
        )}
      </TableHead>
      <TableHead className="px-2 py-2">{/* No filter for Title */}</TableHead>
      <TableHead className="px-2 py-2">
        {/* No filter for Position */}
      </TableHead>
      <TableHead className="px-2 py-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="h-8 w-full justify-start text-left font-normal text-sm"
            >
              <HugeiconsIcon
                icon={Calendar01Icon}
                size={16}
                className="mr-2 shrink-0"
              />
              {dateRange?.from ? (
                dateRange.to ? (
                  <>
                    {format(dateRange.from, 'LLL dd, y')} -{' '}
                    {format(dateRange.to, 'LLL dd, y')}
                  </>
                ) : (
                  format(dateRange.from, 'LLL dd, y')
                )
              ) : (
                <span className="text-muted-foreground">Pick a date range</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              defaultMonth={dateRange?.from}
              selected={dateRange}
              onSelect={handleDateRangeChange}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>
      </TableHead>
      <TableHead className="px-2 py-2">
        <Select
          value={filters.status || 'all'}
          onValueChange={(value) =>
            handleFilterChange('status', value === 'all' ? '' : value)
          }
        >
          <SelectTrigger className="h-8 text-sm bg-background">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </TableHead>
      <TableHead className="px-2 py-2">
        {hasActiveFilters && (
          <div className="flex gap-2">
            {selectedEmployeeIds.length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setSelectedEmployeeIds([])
                }}
                className="h-8 px-2 text-xs"
              >
                <HugeiconsIcon icon={Cancel01Icon} className="mr-1 h-3 w-3" />
                Clear users
              </Button>
            )}
            {(filters.status ||
              filters.dateFrom ||
              filters.dateTo ||
              (!isDirectReportsView && filters.employeeName)) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleClearFilters}
                className="h-8 px-2 text-xs"
              >
                <HugeiconsIcon icon={Cancel01Icon} className="mr-1 h-3 w-3" />
                Clear filters
              </Button>
            )}
          </div>
        )}
      </TableHead>
    </TableRow>
  )
}
